import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Table,
  Space,
  Typography,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Badge,
  Divider,
  Tag,
  Statistic,
  List,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  PrinterOutlined,
  FilterOutlined,
  DollarOutlined,
  UserOutlined,
  TableOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useBillSplitData } from "./useBilll.hook";
import HorizontalMenu from "../../../layout/Horizontalmenu";
import Layout from "../../../layout/Layout";
import { messages } from "../../../../utils/Common/Messages";
import { ApiHelper, getUserData } from "../../../../utils/Helpers";
import "./splitBill.css";
import {
  Customcolumns,
  defaultcolumns,
  Evencolumns,
  getPaymentName,
  Itemcolumns,
  Percentagecolumns,
  Seatcolumns,
} from "./helper";
import SplitByItemCollapsible from "./itemstable";
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const BillSplits = () => {
  const {
    filters,
    setFilters,
    error,
    data,
    loading,
    employees,
    orgTableList,
    pagination,
    BillReportDownload,
    orgSectionList,
  } = useBillSplitData();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [splitBillOrders, setSplitBillOrders] = useState([]);
  const [expandedCheckKeys, setExpandedCheckKeys] = useState([]);
  const userData = getUserData();
  const lan = userData.language ?? "EN";
  // Transform and set data when data changes
  useEffect(() => {
    console.log("Data received:", data);

    if (!loading && !error) {
      if (Array.isArray(data) && data.length > 0) {
        const transformedData = data.map((item) =>
          transformToSplitBillFormat(item)
        );
        setSplitBillOrders(transformedData);
      } else {
        setSplitBillOrders([]);
      }
    }
  }, [loading, data, error]);

  const transformToSplitBillFormat = (apiData) => {
    if (!apiData) return null;

    // Format date from ISO to display format
    const formatDate = (isoString) => {
      const date = new Date(isoString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")} ${String(
        date.getHours()
      ).padStart(2, "0")}:${String(date.getMinutes()).padStart(
        2,
        "0"
      )}:${String(date.getSeconds()).padStart(2, "0")}`;
    };

    // Determine split method display name
    const getSplitMethod = (type) => {
      const splitTypeMap = {
        even: "Even",
        by_item: "By Item",
        by_seat: "By Seat",
        by_percentage: "By Percentage",
        custom: "Custom",
        payment_method: "Payment Method",
        course: "Course",
      };

      return splitTypeMap[type] || "Unknown";
    };

    // Create check items based on split type
    const getCheckItems = (check, splitType) => {
      console.log(splitType);
      if (splitType === "even") {
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "by_item") {
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "by_seat") {
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "by_percentage") {
        // For by_percentage split, you would need actual percentage data
        // Currently returning placeholder since percentages are null in API
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "custom") {
        // For custom split, you would need actual custom split data
        // Currently returning placeholder since custom splits are null in API
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "payment_method") {
        // For payment_method split, you would need actual payment method data
        // Currently returning placeholder since payment methods are null in API
        const items = check.items.map((item, index) => {
          return {
            amount: parseFloat(item.order_product.totalProductAmount),
            discount: parseFloat(item.order_product.discountAmount),
            tax: parseFloat(item.order_product.taxAmount),
            item: item.order_product.productName ?? "item " + (index + 1),
            tip: 0,
          };
        });
        return items;
      } else if (splitType === "course") {
        // For course split, you would need actual course data
        // Currently returning placeholder since courses are null in API
        const items = check.items.map((item, index) => {
          return {
            name: item.order_product.productName ?? "item " + (index + 1),
            price: parseFloat(item.order_product.totalProductAmount),
            quantity: 1,
            index: index + 1,
          };
        });
        return items;
      } else {
        // For unknown split types, return an empty array
        return [];
      }
    };

    function getPaymentDescriptionIfAmountsMatch(apiData, index) {
      // Safely get both amounts
      const paymentAmount =
        Number(apiData?.order?.order_payments?.[index]?.amount) || 0;
      const splitAmount = Number(apiData?.split_checks?.[index]?.amount) || 0;

      // Check if amounts match (with small tolerance for floating point numbers)
      const amountsMatch = Math.abs(paymentAmount - splitAmount) < 0.001;

      // Get the payment description
      const paymentTypeId =
        apiData?.order?.order_payments?.[index]?.paymentTypeId;
      // Return description if amounts match, otherwise return "-"
      return apiData.split_checks[index]?.payments[0]?.payment_type?.code ?? "-";
    }

    function getPaidTime(apiData) {
      // Safely get the order_payments array
      const orderPayments = apiData?.order?.order_payments;

      // If no payments exist, return "-"
      if (
        !orderPayments ||
        !Array.isArray(orderPayments) ||
        orderPayments.length === 0
      ) {
        return "-";
      }

      let latestTime = null;

      // Iterate through all payments to find the latest createDt
      for (const payment of orderPayments) {
        const createDt = payment?.createDt;
        if (createDt) {
          const currentTime = new Date(createDt).getTime();

          // If we don't have a latest time yet, or this time is more recent
          if (!latestTime || currentTime > latestTime.time) {
            latestTime = {
              time: currentTime,
              createDt: createDt,
            };
          }
        }
      }

      // Return the latest creation time if found, otherwise return "-"
      return latestTime?.createDt || "-";
    }

    function getPaymentStatusIfAmountsMatch(apiData, index) {
      // Safely get both amounts
      const paymentAmount =
        Number(apiData?.order?.order_payments?.[index]?.amount) || 0;
      const splitAmount = Number(apiData?.split_checks?.[index]?.amount) || 0;

      // Check if amounts match (with small tolerance for floating point numbers)
      const amountsMatch = Math.abs(paymentAmount - splitAmount) < 0.001;

      // Get the payment description
      const status = apiData?.order?.order_payments?.[index]?.paymentStatus;

      // if (paymentAmount == Number(splitAmount + splitAmount)) {
      //   const status = apiData?.order?.order_payments?.[0]?.paymentStatus;
      //   return status;
      // }
      // // Return description if amounts match, otherwise return "-"
      // return amountsMatch && status ? status : 2;

      const splitCheck = apiData?.split_checks?.[index]?.status;
      if (splitCheck == 'fully_paid') {
        return 1;
      }
      if (splitCheck == 'pending') {
        return 2;
      }

    }

    function getPaymentAmountByStatus(
      payments = []
    ) {
      if (!Array.isArray(payments) || payments.length === 0) return 0;

      return payments.reduce((sum, p) => {
        if (p?.paymentTypeId !== 50) return sum;
        const raw = p?.tipAmount ?? 0;
        const num = Number(raw);
        return sum + (Number.isFinite(num) ? num : 0);
      }, 0);
    }

    const paymentAmont = apiData.split_checks?.reduce((sum, check) => {
      return sum + (getPaymentAmountByStatus(check.payments) || 0);
    }, 0) ?? 0;
    return {
      ...apiData,
      id: `ORD-${apiData.pointepayOrderId}`,
      dateTime: formatDate(apiData.created_at),
      tableNumber: apiData.order?.orgTableId
        ? `${apiData.order?.orgTableName}`
        : "",
      section: apiData.order?.org_table?.section
        ? apiData.order?.org_table?.section?.sectionName
        : "",
      serverName: `${apiData.employee?.firstName || "Unknown"} ${apiData.employee?.lastName || ""
        }`,
      splitMethod: getSplitMethod(apiData.split_type),
      splitType: apiData.split_type,
      updatedAt: formatDate(apiData.updated_at),
      subChecks: apiData.split_checks?.length || 0,
      totalOrderValue: parseFloat(apiData.order?.payableAmount),
      totalCollected: (parseFloat(apiData.order?.paidAmount) || 0) + paymentAmont,
      status: apiData?.order?.orderStatusId,
      paidAt: getPaidTime(apiData),
      checks: apiData.split_checks?.map((check, index) => ({
        id: `Check ${check.check_number}`,
        guestLabel:
          apiData.split_type === "even" || apiData.split_type == "by_seat"
            ? `Seat ${index + 1}`
            : `Guest ${index + 1}`,
        items: check.items ?? 0,
        percentage: parseFloat(check.percentage) ?? 0,
        tipAmount: getPaymentAmountByStatus(check.payments) || 0,
        paymentType: getPaymentDescriptionIfAmountsMatch(apiData, index),
        totalPaid: parseFloat(check.amount),
        status: getPaymentStatusIfAmountsMatch(apiData, index),
        isParent: true,
        children:
          check.items.length > 0
            ? getCheckItems(check, apiData.split_type).map((item, idx) => ({
              key: `item-${index}-${idx}`,
              ...item,
            }))
            : [],
      })),
    };
  };

  const handleRowExpand = (expanded, record) => {
    const keys = expanded
      ? [...expandedRowKeys, record.id]
      : expandedRowKeys.filter((key) => key !== record.id);
    setExpandedRowKeys(keys);
  };

  const handleDownloadReceipt = (check) => {
    console.log(`Downloading receipt for ${check.id}`);
  };

  const handlePrintReceipt = (check) => {
    console.log(`Printing receipt for ${check.id}`);
  };

  const getStatusInfo = (statusCode) => {
    const statusMap = {
      0: { name: "Invalid", color: "#f50" },
      1: { name: "Created", color: "default" },
      2: { name: "Paid", color: "#30649b" },
      3: { name: "Partial Paid", color: "#531dab" },
      4: { name: "Pending", color: "#F7D95F" },
      5: { name: "Voided", color: "#ff4d4f" },
      6: { name: "Retired", color: "#1d39c4" },
    };

    return statusMap[statusCode] || { name: "Unknown", color: "default" };
  };

  const getColumns = (type) => {
    switch (type) {
      case "by_item":
        return Itemcolumns();
      case "custom":
        return Customcolumns();
      case "even":
        return Evencolumns();
      case "by_seat":
        return Seatcolumns();
      case "by_percentage":
        return Percentagecolumns();
      default:
        return defaultcolumns();
    }
  };

  const columns = [
    {
      title: "Date/Time",
      dataIndex: "dateTime",
      key: "dateTime",
      sorter: false,
      render: (text) => (
        <div>
          <div>
            {text.split(" ")[0]} / {text.split(" ")[1]}
          </div>
          {/* <div style={{ fontSize: "12px", color: "#666" }}>
            {text.split(" ")[1]}
          </div> */}
        </div>
      ),
    },
    {
      title: "Order ID",
      dataIndex: "id",
      key: "id",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Table Name",
      dataIndex: "tableNumber",
      key: "tableNumber",
      render: (text) => <Text style={{ color: "#30649b" }}>{text}</Text>,
    },
    {
      title: "Section Name",
      dataIndex: "section",
      key: "section",
      render: (text) => <Text style={{ color: "#30649b" }}>{text}</Text>,
    },
    {
      title: "Server Name",
      dataIndex: "serverName",
      key: "serverName",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Split Method",
      dataIndex: "splitMethod",
      key: "splitMethod",
      render: (method) => {
        const colors = {
          Item: "purple",
          Even: "green",
          Custom: "orange",
        };
        return <Text>{method}</Text>;
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalCollected",
      key: "totalCollected",
      render: (amount) => <Text>${amount.toFixed(2)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "totalCollected",
      key: "totalCollected",
      render: (amount, record) => (
        <div>
          {/* <Text
            strong
            style={{
              color:
                record.totalCollected === record.totalOrderValue
                  ? "#52c41a"
                  : "#fa8c16",
            }}
          >
            ${amount.toFixed(2)}
          </Text> */}
          <div className={`statusText ${getStatusInfo(record.status).name}`}>
            <Tag
              color={getStatusInfo(record.status).color}
              size="small"
              className="mr-0"
            >
              {getStatusInfo(record.status).name}
            </Tag>
          </div>
        </div>
      ),
    },
  ];

  // Handle
  const handleTableChange = (paginationInfo) => {
    if (paginationInfo.current) {
      setFilters({
        ...filters,
        page: paginationInfo.current,
        pageSize: paginationInfo.pageSize,
      });
    }
  };

  return (
    <Layout>
      <HorizontalMenu />
      <div style={{ padding: "0 24px" }}>
        {/* Filters */}
        <div style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]} align="middle" className="filterBox">
            {/* <Col>
              <FilterOutlined style={{ marginRight: 8 }} />
              <Text strong>Filters:</Text>
            </Col> */}
            <Col flex="auto">
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} md={4}>
                  <RangePicker
                    placeholder={["Start Date", "End Date"]}
                    style={{ width: "100%" }}
                    onChange={(dates, dateStrings) => {
                      setFilters({
                        ...filters,
                        fromDate: dateStrings?.[0] || null, // Moment.js object for fromDate
                        toDate: dateStrings?.[1] || null, // Moment.js object for toDate
                      });
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Employee's Name"
                    style={{ width: "100%" }}
                    allowClear
                    onChange={(value) =>
                      setFilters({ ...filters, server: value })
                    }
                  >
                    {employees?.map((employee) => (
                      <Option value={employee.employeeId}>
                        {employee.firstName} {employee.lastName}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Method"
                    style={{ width: "100%" }}
                    allowClear
                    onChange={(value) =>
                      setFilters({ ...filters, splitMethod: value })
                    }
                  >
                    <Option value="even">Even Split</Option>
                    <Option value="by_item">By Item</Option>
                    <Option value="by_seat">By Seat</Option>
                    <Option value="by_percentage">By Percentage</Option>
                    <Option value="custom">Custom Split</Option>
                    <Option value="payment_method">By Payment Method</Option>
                    <Option value="course">By Course</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Section"
                    style={{ width: "100%" }}
                    allowClear
                    onChange={(value) =>
                      setFilters({ ...filters, section: value })
                    }
                  >
                    {orgSectionList?.map((table) => (
                      <Option
                        value={table.orgSectionId}
                        key={table.orgSectionId}
                      >
                        {table.sectionName}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Table"
                    style={{ width: "100%" }}
                    allowClear
                    onChange={(value) =>
                      setFilters({ ...filters, table: value })
                    }
                  >
                    {orgTableList
                      ?.filter(
                        (table) =>
                          !filters.section ||
                          table.orgSectionId === parseInt(filters.section)
                      )
                      ?.filter(
                        (table, index, self) =>
                          index ===
                          self.findIndex(
                            (t) => t.orgTableName === table.orgTableName
                          )
                      )
                      .map((table) => (
                        <Option
                          value={table.orgTableName}
                          key={table.orgTableName}
                        >
                          {table.orgTableName}
                        </Option>
                      ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4} align="right">
                  {
                    <Button
                      type="primary"
                      onClick={() => BillReportDownload()}
                      icon={<DownloadOutlined />}
                    >
                      {messages.Export[lan]}
                    </Button>
                  }
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        {/* Orders Table */}
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "24px" }}>
              <Spin size="large" />
              <div style={{ marginTop: "16px" }}>
                Loading split bill orders...
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={splitBillOrders}
              rowKey="id"
              pageIndex={pagination.current_page}
              expandable={{
                expandedRowKeys: expandedCheckKeys,
                onExpand: (expanded, row) => {
                  setExpandedCheckKeys(expanded ? [row.id] : []);
                },
                expandedRowRender: (record) => {
                  return (
                    <SplitByItemCollapsible
                      data={record.checks}
                      order={record}
                      columns={getColumns(record.splitType)}
                    />
                  );
                },
                // onExpand: handleRowExpand,
                expandRowByClick: true,
                rowExpandable: (record) =>
                  record.checks && record.checks.length > 0,
              }}
              pagination={{
                pageSize: pagination.perPage,
                total: pagination.total,
                current: pagination.currentPage,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["10", "20", "30"],
                showTotal: (total) => `${total} orders`,
                locale: { items_per_page: `/ ${messages.page[lan]}` },
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              locale={{
                emptyText: (
                  <div style={{ padding: "24px" }}>
                    <FileTextOutlined
                      style={{ fontSize: "48px", color: "#bfbfbf" }}
                    />
                    <div style={{ marginTop: "16px" }}>
                      No split bill orders found
                    </div>
                  </div>
                ),
              }}
              className="splitBillTable"
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BillSplits;
